import { ActionTranslator } from './actions/action-translator';
import {
  BookingParams,
  BookingPosition,
  BookingResult,
  PositionContextSummary,
  BookingTransactionId,
  TransactionQueuedBooking,
  SystemContext,
} from './types';
import { ConstraintChecker } from './validation/constraint-checker';
import { RuleDirectionAllowed } from './validation/blockers/rule-direction-allowed';
import { RuleOverbookSourceStock } from './validation/blockers/rule-overbook-source-stock';
import { RuleSourceTargetDifferent } from './validation/blockers/rule-source-target-different';
import { RuleServiceArticleNotOnBufferstock } from './validation/warnings/rule-service-article-not-on-bufferstock';
import { TransactionHandler } from './transaction/transaction-handler';
import { ModelAdapters } from './interface/model-adapters';
import { Log } from './util/log';
import { Validation } from './validation/validation';

/**
 * Zentraler Einstiegspunkt der Buchungs-Pipeline.
 *
 * Stellt zwei unabhängige Methoden bereit, die der Aufrufer (typischerweise
 * eine UI-Page) in dieser Reihenfolge orchestriert:
 *   1. `validate()`: harte und weiche Checks; Blocker verhindern die Buchung,
 *      Warnings erfordern eine User-Bestätigung
 *   2. `book()`: Ausführung der Buchung
 *
 * Kein integriertes `runBooking()`: der Caller komponiert die Schritte selbst,
 * damit UI-Feedback pro Schritt passieren kann.
 *
 * Erweiterungs-Punkte:
 *   - Neue Buchungsrichtung: `actions/action-translator.ts` (`ACTION_MAPPINGS`)
 *   - Neue Regel (Blocker/Warning): `validation/constraint-checker.ts` (Registry-Maps)
 */
export class BookingOrchestrator {
  private readonly actionTranslator: ActionTranslator;
  private readonly checker: ConstraintChecker;
  private readonly transactionHandler: TransactionHandler;

  constructor() {
    this.actionTranslator = new ActionTranslator();
    this.checker = new ConstraintChecker();
    this.transactionHandler = new TransactionHandler(this.actionTranslator);
    this.registerConstraintRules();
  }

  private registerConstraintRules(): void {
    // Blocking
    this.checker.registerAsBlocking(
      RuleDirectionAllowed,
      RuleOverbookSourceStock,
      RuleSourceTargetDifferent,
    );
    // Warnings
    this.checker.registerAsWarning(RuleServiceArticleNotOnBufferstock);
  }

  /**
   * Committet die unter `id` gebündelten Buchungen einer Transaktion.
   *
   * @param id Kennung der Transaktion
   * @param adapters Model-Adapter für den Zugriff auf die Stores
   * @returns Erfolg oder Fehler des Commits
   */
  public async commitTransaction(
    id: BookingTransactionId,
    adapters: ModelAdapters,
  ): Promise<BookingResult> {
    return this.transactionHandler.commit(id, adapters);
  }

  /**
   * Rollt die per `filter` ausgewählten Buchungen einer Transaktion zurück.
   *
   * @param id Kennung der Transaktion
   * @param adapters Model-Adapter für den Zugriff auf die Stores
   * @param systemContext Funktionsschalter-Sicht für die Gegenbuchungen
   * @param filter Prädikat, das die zurückzurollenden Buchungen auswählt
   * @returns Erfolg oder Fehler des Rollbacks
   */
  public async rollbackTransaction(
    id: BookingTransactionId,
    adapters: ModelAdapters,
    systemContext: SystemContext,
    filter: (qb: TransactionQueuedBooking) => boolean,
  ): Promise<BookingResult> {
    return this.transactionHandler.rollback(
      id,
      adapters,
      systemContext,
      filter,
    );
  }

  /**
   * Verwirft eine Transaktion ohne Buchung und ohne Gegenbuchung.
   *
   * @param id Kennung der Transaktion
   * @param adapters Model-Adapter für den Zugriff auf die Stores
   * @returns Erfolg oder Fehler des Verwerfens
   */
  public async discardTransaction(
    id: BookingTransactionId,
    adapters: ModelAdapters,
  ): Promise<BookingResult> {
    return this.transactionHandler.discard(id, adapters);
  }

  /**
   * Prüft eine Position gegen die registrierten Regeln (Blocker und Warnings).
   * Erster Schritt der Pipeline; das Ergebnis wird an `book()` weitergereicht.
   *
   * @param position Zu prüfende Position
   * @param params Buchungs-Parameter (Kontext, Quelle/Ziel)
   * @returns Zusammenfassung mit gefundenen Blockern und Warnings
   */
  public async validate(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<PositionContextSummary> {
    return await this.checker.validate(position, params);
  }

  /**
   * Führt die Buchung aus, indem die zur Buchungsrichtung passende Action
   * ausgewählt und ausgeführt wird. Setzt voraus, dass `validate()` vorher
   * erfolgreich war (siehe Klassen-Doc).
   *
   * @param position Zu buchende Position
   * @param params Buchungs-Parameter (Kontext, Quelle/Ziel)
   * @param evidence Ergebnis von `validate()` (Blocker/Warnings)
   * @returns Erfolg, oder success=false bei Blockern/Fehler
   */
  public async book(
    position: BookingPosition,
    params: BookingParams,
    evidence: Validation
  ): Promise<BookingResult> {
    if (evidence.getSummary(position) === null) {
      return {
        success: false,
        message: 'Buchung blockiert: Position fehlt in Validation'
      };
    }
    if (evidence.hasBlockers()) {
      return {
        success: false,
        message: `Buchung blockiert: ${evidence.getBlockerMessage()}`,
      };
    }
    try {
      const translationResult = await this.actionTranslator.translateToAction(
        position,
        params,
      );
      if (!translationResult.success) {
        return {
          success: false,
          message: `Action failed, reason: ${translationResult.message}`,
          retryable: translationResult.retryable,
        };
      }
      return {
        success: true,
        message: '',
      };
    } catch (error) {
      Log.error('BookingOrchestrator.book', error, {
        source: params.context.booking.sourceType,
        target: params.context.booking.targetType,
      });
      return { success: false, message: 'Unerwarteter Fehler bei der Buchung' };
    }
  }
}
